import React from 'react';
import styled from 'styled-components';
import { edpAnalyticsSyncProductInstructionsUrl } from '../../../externalLinks/productGuide';
import { useAnalyticsSync } from '../../../hooks/analyticsSync/useAnalyticsSync';
import {
  contentPadding,
  fillHeightAndScrollable,
  fullContentWidth,
} from '../../../styles/layout';
import { Description } from '../../Description';
import { Anchor } from '../../Link';
import { Loading } from '../../Loading';
import { Title } from '../styled/fullPageWizard';
import { AnalyticsSyncFooter } from './AnalyticsSyncFooter';
import { AnalyticsSyncInactiveContent } from './AnalyticsSyncInactiveContent';
import { AnalyticsSyncTable } from './AnalyticsSyncTable';

const Content = styled.div`
  ${contentPadding}
  ${fillHeightAndScrollable}
`;

const StyledDescription = styled(Description)`
  max-width: 39rem;
  margin-bottom: 1.375rem;
`;

const TableContainer = styled.div`
  ${fullContentWidth}
  ${fillHeightAndScrollable}
`;

type AnalyticsSyncWizardStepProps = {
  datasetId: string;
  datasetOriginLabel: string;
  organizationId: string;
};

export function AnalyticsSyncWizardStep({
  datasetId,
  datasetOriginLabel,
  organizationId,
}: AnalyticsSyncWizardStepProps) {
  const { analyticsSync, isLoading } = useAnalyticsSync({
    inputDatasetId: datasetId,
    organizationId,
  });

  if (isLoading) {
    return <Loading />;
  }

  if (!analyticsSync) {
    return (
      <AnalyticsSyncInactiveContent
        datasetId={datasetId}
        datasetOriginLabel={datasetOriginLabel}
        organizationId={organizationId}
      />
    );
  }

  return (
    <>
      <Content>
        <Title>Analytics Syncing</Title>
        <StyledDescription>
          When active, data is ready to be synced to analytics and some fields
          are no longer editable. The table below displays all fields and their
          corresponding analytics reference name. To access this data in Sigma,{' '}
          <b>
            <Anchor
              href={edpAnalyticsSyncProductInstructionsUrl}
              target="_blank"
            >
              follow these instructions
            </Anchor>
            .
          </b>
        </StyledDescription>

        <TableContainer>
          <AnalyticsSyncTable
            datasetProperties={analyticsSync.outputDataset.schemaProperties}
          />
        </TableContainer>
      </Content>
      <AnalyticsSyncFooter
        analyticsSync={analyticsSync}
        datasetId={datasetId}
        organizationId={organizationId}
        datasetOriginLabel={datasetOriginLabel}
      />
    </>
  );
}
